import { BankTransaction } from "api/bank-accounts/models";
import { Employee } from "api/other/models";
import { Log } from "api/logbook/models";
import { OrderStageItem } from "api/order-stages/models";
import { BaseUser } from "api/users/models";
import { LoadingStatusPackage, PackageLocations } from "api/warehouse/models";
import { CommentType } from "components/common";
import { Channels, ColorPaletteBackground, ColorPaletteTextColor, CURRENCY_TYPE } from "CONSTANTS";
import { Assign } from "utility-types";
import { Attribute } from "../attributes/models";
import { UUID } from "api/types";

export type OrderPaymentSource = "ON_DELIVERY" | "ONLINE";

export type OrderPaymentType = "CASH_ON_DELIVERY" | "ONLINE" | "DEBIT_CARD" | "INSTALMENT";

export type OrderPaymentStatus = "UNPAID" | "PAID" | "PARTIALLY_PAID" | "OVERPAID";

export type DeliveryMethodType = "self-shipped" | "external-shipment" | "personal-collection";

export enum CreatePickingOrLoadingSource {
  ROUTE = "ROUTE",
  ORDER_GROUP = "ORDER_GROUP",
}

type Kind = "REPLACEMENT" | "REPAIR" | "SUPPLEMENT" | "RESIGNATION";
type Direction = "TO_BE_PICKED_UP" | "TO_BE_DELIVERED";
type WarrantyStatus = "WITH_WARRANTY" | "WITHOUT_WARRANTY" | "";
export type OrderType = "STANDARD" | "COMPLAINT";
export type PaymentProvider = "PAYU" | "P24";

export type Status =
  | "RECEIVED"
  | "PROCESSING"
  | "ROUTE_READY"
  | "ON_ROUTE"
  | "DELIVERED"
  | "SETTLED"
  | "CANCELED";

export interface Order {
  name: string;
  created: string;
  isInvoiceRequired: boolean;
  isSeen: boolean;
  isExternalRelease: boolean;
  createdBy: { id: number; firstName: string; lastName: string; avatar: string } | null;
  collectionHours: {
    collectionDate: string | null;
    maximumCollectionHour: string | null;
    minimumCollectionHour: string | null;
    plannedCollectionHour: string | null;
  };
  deliveryConfirmationDeadline: string | null;
  deliveryDateConfirmTimestamp: string | null;
  deliveryDateConfirmationSource: string;
  deliveryDateConfirmedBy: BaseUser;
  isCanceled: boolean;
  isExternal: boolean;
  isConfirmed: boolean;
  isSettled: boolean;
  signature: string;
  warehouseDeliveryDate: string | null;
  isInWarehouse: boolean;
  id: number;
  complaintPictures: ComplaintPicture[];
  parent: {
    created: string;
    signature: string;
    uuid: string;
    numberOfCreatedComplaints: number;
  } | null;
  uuid: string;
  receipt: {
    externalId: string;
    id: number;
    isPrinted: boolean;
    issued: string | null;
    lastModified: string | null;
    lastModifiedBy: BaseUser | null;
    lastPrinted: string | null;
    lastPrintedBy: BaseUser | null;
    order: number;
  } | null;
  shipmentTime: string;
  description: string;
  totalPrice: number;
  itemsPrice: number;
  numberOfPackages: number;
  isConfirmedInExternalService: boolean;
  isCollectionDateConfirmed: boolean;
  isDeliveryDateConfirm: boolean;
  hasTransportBeenInvoiced: boolean;
  hasItemsBeenInvoiced: boolean;
  hasTransportBeenSettled: boolean;
  hasItemsBeenSettled: boolean;
  hasExternalFulfillment: boolean;
  itemsInvoicedDate: string | null;
  maximumCollectionHourAtConfirmation: string;
  minimumCollectionHourAtConfirmation: string;
  transportInvoicedDate: string | null;
  itemsSettledDate: string | null;
  transportSettledDate: string | null;
  kind: Kind;
  direction: Direction;
  warrantyStatus: WarrantyStatus;
  numberOfDaysFromCreatedDate: number;
  hasCarrying: boolean;
  hasSmsSent: boolean;
  customer: {
    id: number;
    canAddNewIndexes: boolean;
    name: string;
    hasDropShipping: boolean;
    hasAmountToCollect: boolean;
  } | null;
  collectionDate: string | null;
  assignedTo: BaseUser | null;
  leftDays: number;
  items: OrderProductInstance[];
  comments: CommentType[];
  payment: {
    id: number;
    status: OrderPaymentStatus;
    type: OrderPaymentType;
    isVerificationRequired: boolean;
    isReturn: boolean;
    externalId: string;
    provider: PaymentProvider;
    discount: number | string;
    paidAmount: number | string;
    externalAmount: number | string;
    amountToCollect: number | string;
    currency: string;
    finishedAt: string | null;
  };
  orderStages: OrderStageItem[];
  missingAmount: string;
  isTransportOrder: boolean;
  salesAccount: {
    channel: string;
    id: number;
    name: string;
    color: ColorPaletteBackground;
    textColor: ColorPaletteTextColor;
    currency: CURRENCY_TYPE;
  };
  invoice: {
    id: number;
    street: string;
    city: string;
    postCode: string;
    firstName: string;
    lastName: string;
    companyName: string;
    taxId: string;
    email: string | null;
  } | null;
  offers:
    | {
        id: number;
        itemId: number;
        externalId: string;
        url: string;
        isAssigned: boolean;
      }[]
    | null;
  miloOffers:
    | {
        id: number;
        itemId: number;
        externalId: string;
        url: string;
        isAssigned: boolean;
      }[]
    | null;

  messageToSeller: string;
  recentManufacturersOrders: ManufacturerOrder[];
  recentProductionOrders: ProductionOrder[];
  delivery: Delivery;
  isVisited: boolean;
  type: OrderType;
  status: Status;
  statusTemp: StatusTemp;
  followers: { id: number; avatar: string; firstName: string; lastName: string }[];
  reservations: { id: number; signature: string; created: string }[];
  route: {
    id: number;
    signature: string;
    name: string;
    departureDate: string;
    departureTime: string;
    loadingDate: string;
    loadingTime: string;
    length: number;
    driver: {
      id: number;
      userId: number;
      firstName: string;
      lastName: string;
      avatar: string;
      phone: string;
    };
  } | null;
  onWhichRoutesItWas: {
    id: number;
    signature: string;
    departureDate: string;
    departureTime: string;
    driver: {
      id: number;
      avatar: string;
      userId: number;
      lastName: string;
      firstName: string;
    };
  }[];
  inWhichGroupsItWas: {
    id: number;
    signature: string;
    name: string;
    pickUpDate: string;
    pickUpTime: string;
  }[];
  orderGroup: {
    id: number;
    signature: string;
    name: string;
    pickUpDate: string;
    pickUpTime: string;
  };
  isCallRequired: boolean;
  isWaitingForCustomerCall: boolean;
  isShipped: boolean;
  recentLogs: Log[];
  hasProducedItems: boolean;
  totalVolume: number;
  totalWeight: number;
  isMerged: boolean;
  isSynchronizedWithBaselinker: boolean;
  baselinkerId: number;
  priority: string;
  placedAt: string;
  statusTrackingLink: string;
  statusTrackingToken: string;
  isDelivered: boolean;
  deliveredDate: string | null;
  dateOfDelivered: string | null;
}

export interface OrderPatch {
  statusTemp?: number;
}

export interface OrderPost {
  customer?: number | null;
  transactionId: string;
  salesAccount: number;
  route: number | null;
  isCanceled?: boolean;
  description?: string;
  name?: string;
  isExternalRelease?: boolean;
  assignedTo?: number | null;
  isSeen?: boolean;
  isShipped?: boolean;
  hasCarrying?: boolean;
  isSettled?: boolean;
  shipmentTime?: string;
  isTransportOrder?: boolean;
  collectionDate: string | null;
  payment: {
    type: OrderPaymentType;
    source: OrderPaymentSource;
    externalId: string;
    provider: string;
    status: "UNPAID" | "PAID" | "PARTIALLY_PAID" | "OVERPAID";
    discount: number;
    paidAmount: number;
    currency: string;
  };
  invoice?: {
    street: string;
    city: string;
    postCode: string;
    firstName: string;
    companyName: string;
    lastName: string;
    taxId: string;
    email?: string;
  } | null;
  delivery: {
    firstName: string;
    lastName: string;
    phone: string;
    street: string;
    city: string;
    countryCode: string;
    postCode: string;
    method: string;
    amount: string;
    companyName: string;
    clientLogin?: string;
    email?: string;
  };
  isInvoiceRequired: boolean;
  isConfirmed: boolean;
  messageToSeller: string;
  comments: {
    body: string;
  }[];
  items: {
    product: number;
    currency: string;
    index: number;
  }[];
  isCallRequired?: boolean;
  isWaitingForCustomerCall?: boolean;
  hasProducedItems?: boolean;
  priority?: string;
}

export interface Delivery {
  coordinates: { lat: number; lng: number };
  minimumDeliveryDate: string | null;
  minimumDeliveryTime: string;
  maximumDeliveryTime: string;
  plannedDeliveryTime: string | null;
  companyName: string;
  countryCode?: string;
  customer: number | null;
  firstName: string;
  lastName: string;
  postCode: string;
  currency: string;
  method: DeliveryMethodType;
  street: string;
  amount: string;
  phone: string;
  city: string;
  id: number;
  clientLogin?: string;
  shippingService: number | null;
  shippingServiceDisplay: string | null;
  email: null | string;
}

export interface CommentPost {
  body: string;
  isOnlyVisibleForTeam?: boolean;
  order: number | string;
}

export interface OrderCategory {
  count: number;
  id: number;
  name: string;
}

export interface OrderPoint {
  id: number;
  point: {
    lat: number;
    lng: number;
  };
  isHidden: boolean;
  hideOnMapTo: string | null;
  hideUntilIssueIsSolved: boolean;
  leftDays: number;
  numberOfDaysFromCreatedDate: number;
  hasUpholstery: boolean;
  type: OrderType;
  countryName: string;
  priority: string;
  warehouseDeliveryDetails: WarehouseDeliveryDetails;
}

export interface WarehouseDeliveryDetails {
  daysLeftToDelivery: number | null;
  isInWarehouse: boolean;
  date: string | null;
}

export interface GroupOrderPoint {
  isHidden: boolean;
  hideOnMapTo: string | null;
  hideUntilIssueIsSolved: boolean;
  id: number;
  type: OrderType;
  point: {
    lng: number;
    lat: number;
  };
  leftDays: number;
  numberOfDaysFromCreatedDate: number;
  hasUpholstery: boolean;
  method: DeliveryMethodType;
  shippingService: null | string;
  priority: string;
  warehouseDeliveryDetails: null;
}

export interface ListOrder {
  isConfirmed: boolean;
  isCollectionDateConfirmed: boolean;
  isInvoiceRequired: boolean;
  isDeliveryDateConfirm: boolean;
  hasTransportBeenInvoiced: boolean;
  kind: Kind;
  receipt: {
    externalId: string;
    id: number;
    isPrinted: boolean;
    issued: string | null;
    lastModified: string | null;
    lastModifiedBy: BaseUser | null;
    lastPrinted: string | null;
    lastPrintedBy: BaseUser | null;
    order: number;
  } | null;

  direction: Direction;
  warrantyStatus: WarrantyStatus;
  hasItemsBeenInvoiced: boolean;
  numberOfDaysFromCreatedDate: number;
  uuid: string;
  name: string;
  hasTransportBeenSettled: boolean;
  hasItemsBeenSettled: boolean;
  itemsSettledDate: string | null;
  transportSettledDate: string | null;
  itemsInvoicedDate: string | null;
  transportInvoicedDate: string | null;
  isShipped: boolean;
  dateOfDelivered: string | null;
  isSettled: boolean;
  signature: string;
  isSeen: boolean;
  hasSmsSent: boolean;
  smsSentTimestamp: string | null;
  deliveryDateConfirmTimestamp: string | null;
  id: number;
  customer: {
    id: number;
    name: string;
    textColor: string;
    color: string;
    hasDropShipping: boolean;
  } | null;
  isConfirmedInExternalService: boolean;
  assignedTo: BaseUser | null;
  deliveryAddress: {
    clientLogin?: string;
    countryCode?: string | null;
    city: string;
    postCode: string;
    street: string;
  };
  type: OrderType;
  status: Status;
  statusTemp: StatusTemp;
  salesAccount: {
    channel: string;
    id: number;
    name: string;
    color: ColorPaletteBackground;
    textColor: ColorPaletteTextColor;
    currency: CURRENCY_TYPE;
  };
  client: {
    firstName: string;
    lastName: string;
    phone: string;
    companyName: string;
    hasDropShipping: boolean;
  };
  created: string;
  placedAt: string;
  leftDays: number;
  isCallRequired: boolean;
  isWaitingForCustomerCall: boolean;
  paymentStatus: Order["payment"]["status"];
}

export interface OrderProductInstanceAttribute {
  id: number;
  name: string;
  description: string;
  kind: Exclude<Attribute["kind"], "FABRIC">;
  values: {
    isAssignableToIndex: boolean;
    picture: string | null;
    name: string;
    id: number;
  }[];
}

export interface OrderProductInstanceFabricAttribute {
  id: number;
  name: string;
  description: string;
  kind: "FABRIC";
  categories: {
    name: string;
    values: {
      picture: string | null;
      isAssignableToIndex: boolean;
      name: string;
      id: number;
    }[];
  }[];
}

export interface OrderProductInstance {
  id: number;
  product: {
    id: number;
    ean: string;
    name: string;
    picture: string;
    attributes: (OrderProductInstanceAttribute | OrderProductInstanceFabricAttribute)[];
    indexes: { [key: string]: number };
    isAddedToComplaint: null;
    category: number | null;
  };
  constantValues: number[];
  deliveryUnitAmount: string;
  packages: {
    id: number;
    package: number;
    internalId: string;
    name: string;
    isAddedToComplaint: boolean;
    quantity: number;
  }[];
  isPrepared: boolean;
  isReserved: boolean;
  isReleasedFromStock: boolean;
  isAddedToComplaint: boolean;
  maxComplaintQuantity: number;
  numberOfPreparedUpholsteries: number;
  note: string;
  externalNumber: string;
  name: string;
  index: number;
  order: number;
  quantity: number;
  amount: string;
  productSetCode: string;
  productSetName: string;
  currency: CURRENCY_TYPE;
  boughtAt: null | string;
  unitAmount: string | null;
  weight: number;
  offer: {
    id: number;
    name: string;
    externalId: string;
  };
}

export interface UpholsteryOrder {
  id: number;
  salesAccount: {
    id: number;
    name: string;
    channel: string;
    color: ColorPaletteBackground;
    textColor: ColorPaletteTextColor;
    currency: CURRENCY_TYPE;
  };
  leftDays: number;
  signature: string;
  priority: "low";
  created: string;
  productionOrders: {
    upholsterer: Employee | null;
    seamstress: Employee | null;
    cutter: Employee | null;
    signature: string;
    id: number;
    name: string;
    delegations: {
      isCut: boolean;
      isSewn: boolean;
      isPrepared: boolean;
    };
    attributes: {
      attribute: string;
      value: string;
    }[];
    comments: {
      id: number;
      body: string;
      user: {
        id: number;
        avatar: string;
        firstName: string;
        lastName: string;
        phone: string;
      };
      created: string;
    }[];
    isSendToManufacturer: boolean;
    isReceivedFromManufacturer: boolean;
    isReadyToCollectFromManufacturer: boolean;
  }[];
  client: {
    firstName: string;
    lastName: string;
    phone: string;
    companyName: string;
  };
  deliveryAddress: {
    clientLogin?: string;
    postCode: string;
    city: string;
    street: string;
  };
  isReceivedFromManufacturer: boolean;
  isSendToManufacturer: boolean;
}

export interface SplittedOrder {
  id: number;
  signature: string;
  price: number;
  weight: number;
  productCount: number;
  itemsCount: number;
  items: {
    index: number;
    quantity: number;
    amount: number;
    currency: string;
    boughtAt: string | null;
    id: number;
    product: { id: number; picture: string; name: string };
  }[];
}
export type SplittedOrders = [SplittedOrder, SplittedOrder];

export interface OrderCollectionDateConfirmation {
  collectionDate: string | null;
  plannedCollectionHour: string | null;
  deliveryConfirmationDeadline: string | null;
  minimumCollectionHour: string | null;
  maximumCollectionHour: string | null;
  phone: string | null;
  email: string | null;
  officeOpeningHours: string;
  language: "pl" | "de" | "at";
  order: {
    items: {
      id: number;
      quantity: number;
      amount: number;
      product: { id: number; name: string };
    }[];
    signature: string;
    customer: Order["customer"];
    payment: {
      paidAmount: number;
      amountToCollect: number;
      discount: string | number;
      currency: string;
    };
    delivery: {
      firstName: string;
      amount: number;
      lastName: string;
      phone: string;
      street: string;
      city: string;
      postCode: string;
      email: string;
    };
    numberOfPackages: number;
    totalWeight: number;
  };
}
export interface OrderTrackingInfo {
  car: {
    coordinates: {
      lat: number;
      lng: number;
    };
  } | null;
  order: {
    status: Order["status"];
    statusText: string;
    plannedDeliveryTime: string;
    coordinates: {
      lng: number;
      lat: number;
    };
  };
  contact: {
    firstName: string;
    lastName: string;
    phone: string;
  };
}
export interface OrderForRoute {
  id: number;
  created: string;
  delivery: {
    id: number;
    postCode: string;
    isHidden: boolean;
    hideIssue: string;
    hideOnMapTo: string | null;
    hideUntilIssueIsSolved: boolean;
    city: string;
    street: string;
    price: { amount: number; currency: CURRENCY_TYPE };
    point: { lng: number; lat: number };
    distance: number | null;
    time: number | null;
    plannedDeliveryTime: string | null;
    shippingService: string | null;
    stopoverTime: number;
    countryName: string;
    amount: number;
    companyName: string;
    firstName: string;
    lastName: string;
    maximumDeliveryTime: string | null;
    minimumDeliveryTime: string | null;
    minimumCollectionHour: string | null;
    maximumCollectionHour: string | null;
    minimumCustomDeliveryHour: string | null;
    maximumCustomDeliveryHour: string | null;
    phone: string;
  };
  customer: Order["customer"];
  signature: string;
  comments: Order["comments"];
  items: {
    id: number;
    index: number;
    amount: number;
    quantity: number;
    product: { id: number; category: number; name: string };
    attributes: { attribute: string; value: string }[];
  }[];
  leftDays: number;
  numberOfDaysFromCreatedDate: number;
  type: Order["type"];
  totalVolume: Order["totalVolume"];
  totalWeight: Order["totalWeight"];
  hasProducedItems: boolean;
  hasCarrying: boolean;
  hasSmsSent: boolean;
  hasEmailSent: boolean;
  deliveryDateRejectedByAvatar: string | null;
  deliveryDateRejectedByFirstName: string;
  deliveryDateRejectedByLastName: string;
  maximumCollectionHourAtConfirmation?: string;
  minimumCollectionHourAtConfirmation?: string;
  deliveryDateRejectedTimestamp: string | null;
  isDeliveryDateConfirm: boolean;
  isDeliveryDateRejected: boolean;
  collectionHours: Order["collectionHours"];
  messageToSeller: string;
  hasReservations: boolean;
  hasUpholstery: boolean;
  isSettled: boolean;
  isCanceled: boolean;
  prices: { PLN: number };
  client: {
    firstName: string;
    lastName: string;
    phone: string;
    companyName: string;
    email: string;
    hasDropShipping: boolean;
  };
  payment: {
    id: number;
    discount: number;
    paidAmount: number;
    status: OrderPaymentStatus;
    type: OrderPaymentType;
  };
  isConfirmed: boolean;
  method: "external-shipment" | "personal-collection" | "self-shipped";
  phone: string;
  priority: string;
  paymentType: OrderPaymentType;
  externalId: string | null;
  warehouseDeliveryDetails: WarehouseDeliveryDetails;
}

export interface ManufacturerOrder {
  created: string;
  createdFor: string;
  id: number;
  isSentToManufacturer: boolean;
  isFinished: boolean;
  link: string;
  manufacturer: { name: string };
  internalId: string;
  sent: number;
  isConfirmed: boolean;
  signature: string;
  total: number;
}

export interface ProductionOrder {
  created: string;
  createdFor: string;
  id: number;
  internalId: string;
  isFinished: boolean;
  name: string;
  signature: string;
}

export interface OrderIndex {
  id: number;
  indexId: number;
  name: string;
  orderSignature: string;
  created: string;
  orderId: number;
  isSelfProduced: boolean;
  manufacturer: {
    id: number;
    name: string;
  } | null;
  attributes: {
    attribute: string;
    value: string;
  }[];
  isSent: boolean;
}

export interface DailyStatistics {
  id: number;
  name: string;
  routeCount: number;
  orderGroupCount: number;
}

export type OrdersIndexPopularity = Record<
  string,
  {
    indexId: number;
    indexQuantity: number;
    indexVerboseAttributesValues: string;
    productId: number;
    productCategoryId: number;
    productName: string;
    manufacturer: { id: number; name: string } | null;
  }[]
>;

export interface PostReservationResponse {
  errors: Record<number, number>;
  reservations: { id: number; signature: string; created: string }[];
}

export interface OrderPointsForRoute {
  results: OrderPoint[];
  categories: OrderCategory[];
  count: number;
}

export interface OrderItemsSearch {
  id: number;
  name: string;
  signature: string;
  delivery: {
    id: number;
    street: string;
    city: string;
    postCode: string;
    point: { lat: number; lng: number };
    firstName: string;
    lastName: string;
  };
  route: { id: number; signature: string; color: string } | null;
  customer: {
    color: string;
    hasDropShipping: boolean;
    id: number;
    name: string;
    textColor: string;
  } | null;
  externalId: string | null;
}

export interface OrderBankTransactionsSearch {
  client: {
    firstName: string;
    lastName: string;
    street: string;
    city: string;
    postCode: string;
    phone: string;
  };
  currency: CURRENCY_TYPE;
  id: number;
  isAdded: boolean;
  signature: string;
  totalAmount: string;
  transactionsExternalIds: string[];
}

export interface OrderNearestRoutes {
  routes: {
    id: number;
    signature: string;
    name: string;
    driver: {
      id: number;
      userId: number;
      firstName: string;
      lastName: string;
      avatar: string | null;
      phone: string;
    } | null;
    departureDate: string | null;
    departureTime: string | null;
    loadingDate: string | null;
    loadingTime: string | null;
    length: number;
  }[];
}

export interface OrderAdditionalData {
  id: number;
  uuid: string;
  signature: string;
  joinedOrders:
    | {
        id: number;
        uuid: string;
        signature: string;
        created: string;
        assignedTo: null | { avatar: string | null; firstName: string; lastName: string };
      }[]
    | null;
}

export interface JoinedOrder {
  id: number;
  signature: string;
  uuid: string;
  name: string;
  customer: { id: number; name: string; hasAmountToCollect: boolean } | null;
  priceList: {
    color: string;
    currency: CURRENCY_TYPE;
    id: number;
    name: string;
    textColor: string;
  } | null;
  salesAccount: string | null;
  assignedTo: {
    id: number;
    avatar: string | null;
    firstName: string;
    lastName: string;
    phone: string;
    isActive: boolean;
    isSuperuser: boolean;
  };
  isMerged: boolean;
  delivery: {
    id: number;
    companyName: string;
    firstName: string;
    lastName: string;
    phone: string;
    street: string;
    city: string;
    postCode: string;
    amount: number | string;
    currency: CURRENCY_TYPE;
    clientLogin: string;
    email: string;
    method: Order["delivery"]["method"];
  };
  payment: {
    id: number;
    type: OrderPaymentType;
    status: Order["payment"]["status"];
    source: OrderPaymentSource;
    paidAmount: number | string;
    discount: number | string;
    currency: CURRENCY_TYPE;
    amountToCollect: number | string;
  };
  items: {
    amount: number | string;
    attributesValues: string;
    currency: CURRENCY_TYPE;
    id: number;
    name: string;
    note: string;
    quantity: number;
  }[];
  messageToSeller: string;
  comments: {
    body: string;
    created: string;
    id: number;
    user: {
      avatar: string | null;
      firstName: string;
      id: number;
      isActive: boolean;
      isSuperuser: boolean;
      lastName: string;
      phone: string;
    };
  }[];
}
export interface ComplaintParent {
  parent: {
    signature: string;
    id: number;
    uuid: string;
    numberOfComplaints: number;
    created: string;
    otherComplaints: {
      signature: string;
      id: number;
      uuid: string;
      isCanceled: boolean;
      isSettled: boolean;
      createdBy: {
        id: number;
        avatar: string | null;
        firstName: string;
        lastName: string;
        phone: string;
        isActive: boolean;
        isSuperuser: boolean;
      };
      created: string;
    }[];
  };
}
export interface ComplaintPicture {
  complaint: number;
  description: string;
  file: string;
  id: number;
  name: string;
}

export interface ComplaintPost {
  parent: number | null;
  reason: string;
  direction: string;
  kind: Kind;
  warrantyStatus: WarrantyStatus;
  description?: string;
  customer?: number;
  salesAccountId?: number | null;
  items: {
    id: number;
    quantity: number;
    productId: number;
    maxComplaintQuantity: number;
    isAddedToComplaint: boolean;
    indexId: number;
    note: string;
  }[];
  delivery?: {
    firstName: string;
    externalAddressNumber: string;
    lastName: string;
    country: string;
    phone: string;
    email: string;
    street: string;
    city: string;
    postCode: string;
    point: { lat: number; lng: number } | null;
    companyName: string;
  };
}

export interface PostDevelopmentOrdersSchema {
  numberOfOrders: number;
  items: {
    minRangeNumber: number;
    maxRangeNumber: number;
    minPrice: string;
    maxPrice: string;
    minQuantity: number;
    maxQuantity: number;
    includeSets: boolean;
  };
  marketplaces: {
    minNumberOfOrders: number;
    maxNumberOfOrders: number;
    channels: Channels[];
  };
  customers: {
    minNumberOfOrders: number;
    maxNumberOfOrders: number;
    includeDropshippingCustomers: boolean;
  };
  deliveries: {
    minPrice: string;
    maxPrice: string;
  };
  payments: {
    maximumDiscount: string;
    types: OrderPaymentType[];
    selectedCurrencies: CURRENCY_TYPE[];
  };
  whichOfficeEmployees: string[];
}
type UserPartial = {
  id: number;
  avatar: string | null;
  firstName: string;
  lastName: string;
} | null;

export interface OrderMessagesSchema {
  order: { id: number; signature: string; salesAccount: number };
  client: { firstName: string; lastName: string; avatar: string | null };
  messages: {
    id: number | string;
    status: "SENT" | "AWAITING" | "DELIVERED" | "UNSENT" | "UNDELIVERED";
    timestamp: string | null;
    body: string;
    wayOfCommunication: "OUTGOING" | "INCOMING";
    isRead: boolean;
    readAt: string | null;
    readBy: UserPartial;
    createdBy: UserPartial;
    sentToPhoneNumber: string;
    failureReason: string;
    created: string;
  }[];
}

export type Point = { lat: number; lng: number };

export interface OrderDeliveryInfo {
  items: {
    id: number;
    isDelivered: boolean;
    point: Point;
    distanceToDeliveryPoint: number;
  }[];
  notDeliveredReasons: {
    id: number;
    createdBy: { id: number; firstName: string; lastName: string };
    body: string;
  }[];
  driverNotes: {
    body: string;
    createdBy: { id: number; firstName: string; lastName: string };
    duration: number;
    file: string;
    id: number;
    title: string;
  }[];
  signatures: {
    id: number;
    createdBy: { id: number; firstName: string; lastName: string };
    point: Point;
    distanceToDeliveryPoint: number;
    url: string;
    createdAt: string;
  }[];
  photos: {
    id: string;
    url: string;
    point: Point;
    distanceToDeliveryPoint: number;
    createdAt: string;
    createdBy: { id: number; firstName: string; lastName: string };
  }[];
}

export interface OrderTransactions {
  id: number;
  signature: string;
  paymentDate: string | null;
  client: {
    firstName: string;
    lastName: string;
    phone: string;
    address: { street: string; postCode: string; city: string };
  };
  bankTransactions: Assign<
    BankTransaction,
    {
      addedBySource: "MANUALLY" | "IMPORTED" | "FETCHED_FROM_API";
      created: string;
      relationId: number;
      createdBy: {
        avatar: string;
        firstName: string;
        id: number;
        lastName: string;
      };
    }
  >[];
}

export interface Listings {
  filledListings: {
    listing: {
      name: string;
      attributes: string;
      externalId: string;
      ean: string;
      quantity: number;
      amount: string;
      currency: string;
    };
    items: {
      name: string;
      attributes: string;
      quantity: number;
      amount: number;
      currency: string;
      externalId: string;
      ean: string;
    }[];
  }[];
  emptyListings: {
    name: string;
    attributes: string;
    externalId: string;
    ean: string;
    quantity: number;
    amount: string;
    currency: string;
  }[];
  itemsWithoutListings: {
    name: string;
    attributes: string;
    quantity: number;
    amount: number;
    currency: string;
    externalId: string;
    ean: string;
  }[];
  salesAccount: {
    channel:
      | "allegro"
      | "amazon"
      | "arena"
      | "b2b"
      | "eBay"
      | "erli"
      | "hood"
      | "kaufland"
      | "magento"
      | "moebel24"
      | "presta"
      | "shoper"
      | "shopify"
      | "wayfair";
    color: string;
    currency: string;
    id: number;
    name: string;
    textColor: string;
  };
}

export interface LoadingStatus {
  hasLoadingCompleted: boolean;
  items: {
    id: number;
    product: {
      id: number;
      picture: string | null;
      name: string;
      category: string | null;
    };
    index: number;
    quantity: number;
    amount: number;
    currency: string;
    boughtAt: string | null;
    packages: LoadingStatusPackage[];
  }[];
}

export interface OrderStatusBulkUpdateDTO {
  orders: number[];
  isDelivered: boolean;
  isVisited: boolean;
}

export interface StatusTemp {
  color: string;
  icon: string | null;
  id: number;
  meta: {};
  name: string;
  position: number;
}

export interface OrderStatusTempBulkUpdateDTO {
  orders: number[];
  statusTemp: number;
}

export interface B2bWarehouseDeliveryDateBulkUpdate {
  objects: number[];
  warehouseDeliveryDate: string;
}

export interface OrderIsInWarehouseBulkUpdate {
  warehouseDeliveryDate: string | null;
  isInWarehouse: boolean;
  orders: number[];
}

export interface UpdateCustomerBulkUpdate {
  customer: number | null;
  orders: number[];
}

enum LocationType {
  "RAMP" = "RAMP",
  "WAREHOUSE" = "WAREHOUSE",
}

export enum ManufacturingStatus {
  NOT_SCHEDULED = "NOT_SCHEDULED",
  AWAITING = "AWAITING",
  IN_PROGRESS = "IN_PROGRESS",
  FINISHED = "FINISHED",
}

export enum WarehouseStatus {
  IN_STOCK = "IN_STOCK",
  OUT_OF_STOCK = "OUT_OF_STOCK",
}

export enum WarehouseReceivedStatus {
  NOT_RECEIVED = "NOT_RECEIVED",
  RECEIVED = "RECEIVED",
}

export enum WarehouseReleasedStatus {
  NOT_RELEASED = "NOT_RELEASED",
  RELEASED = "RELEASED",
}

enum ReturnStatus {
  NOT_RETURNED = "NOT_RETURNED",
  RETURNED = "RETURNED",
}

export enum DeliveryStatus {
  ON_ROUTE = "ON_ROUTE",
  NOT_DELIVERED = "NOT_DELIVERED",
  DELIVERED = "DELIVERED",
}

export interface SingleItemQuantities {
  id: UUID;
  index: {
    id: number;
    product: {
      id: number;
      name: string;
      category: string;
    };
    attributes: {
      id: number;
      attribute: string;
      value: string;
    }[];
  };
  item: {
    id: number;
    quantity: number;
  };
  labelDownloadedAt: string | null;
  labelDownloadedBy: string | null;
  position: number;
  statuses: QuantityStatuses;
}

export interface SingleItemQuantitiesDetails {
  id: UUID;
  statuses: QuantityStatuses;
  item: {
    id: number;
    quantity: number;
  };
  index: {
    id: number;
    product: {
      id: number;
      name: string;
      category: string;
    };
    attributes: {
      id: number | string;
      attribute: string;
      value: string;
    }[];
  };
  labelDownloadedAt: string | null;
  packages: {
    id: string;
    package: {
      id: number;
      name: string;
      description: string;
      internalId: string;
    };
    code: string;
    warehouse: {
      id: number;
      name: string;
    };
    location: Assign<
      Omit<PackageLocations["locations"][number], "path" | "warehouseId" | "warehouse" | "code">,
      {
        type: LocationType;
        name: string;
      }
    >;
    uniquePackage: string | null;
    statuses: QuantityStatuses;
  }[];
  labelDownloadedBy: string | null;
  position: number;
  locations: Assign<
    Omit<PackageLocations["locations"][number], "path" | "warehouseId" | "warehouse" | "code">,
    {
      type: LocationType;
      name: string;
    }
  >[];
}

type QuantityStatuses = {
  manufacturing: {
    value: ManufacturingStatus;
    requested: number;
    completed: number;
  };
  warehouse: {
    value: WarehouseStatus;
  };
  warehouseReceived: {
    value: WarehouseReceivedStatus;
    requested: number;
    completed: number;
  };
  warehouseReleased: {
    value: WarehouseReleasedStatus;
    requested: number;
    completed: number;
  };
  return: {
    value: ReturnStatus;
    requested: number;
    completed: number;
  };
  delivery: {
    value: DeliveryStatus;
    requested: number;
    completed: number;
  };
};

export interface ItemsWithSingleQuantities {
  customMadeLabelDownloadedAt: string | null;
  customMadeLabelDownloadedBy: null;
  id: number;
  index: {
    id: number;
    product: {
      id: number;
      name: string;
      category: string;
    };
    attributes: {
      id: number;
      attribute: string;
      value: string;
    }[];
  };
  quantity: number;
  statuses: QuantityStatuses;
  locations: Assign<
    Omit<PackageLocations["locations"][number], "path" | "warehouseId" | "warehouse" | "code">,
    {
      type: LocationType;
      name: string;
    }
  >[];
}

export interface CreatePickingOrLoading {
  id: string | number;
  signature: string;
  kind: CreatePickingOrLoadingSource;
}
